import { useMemo, useState } from 'react';
import assetCatalog from '../../../data/asset-catalog.json';

const ASSET_DISPLAY_LIMIT = 120;
const POPULAR_STOCK_LIMIT = 80;
const assetFilterOptions = [
    { type: 'crypto', label: 'Crypto' },
    { type: 'etf', label: 'ETFs' },
    { type: 'stock', label: 'Stocks' },
    { type: 'forex', label: 'Forex' },
];
const defaultActiveAssetTypes = assetFilterOptions.reduce(
    (filters, option) => ({
        ...filters,
        [option.type]: false,
    }),
    {}
);
const getDisplayGroup = (asset) => {
    if (asset.type === 'stock' && asset.rank <= POPULAR_STOCK_LIMIT) {
        return 0;
    }

    if (asset.type === 'crypto') {
        return 1;
    }

    if (asset.type === 'etf') {
        return 2;
    }

    if (asset.type === 'stock') {
        return 3;
    }

    if (asset.type === 'forex') {
        return 4;
    }

    return 5;
};
const getRank = (asset) => asset.rank || Number.MAX_SAFE_INTEGER;

const assets = assetCatalog
    .map((asset) => {
        let logoUrl = null;
        if (asset.logo) {
            try {
                logoUrl = require(`../../../assets/images/logos/${asset.logo}`);
            } catch {
                logoUrl = null;
            }
        }
        return {
            ...asset,
            logoUrl,
            searchText: [
                asset.symbol,
                asset.name,
                asset.type,
                asset.exchange,
                asset.currency,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase(),
        };
    })
    .sort(
        (a, b) =>
            getDisplayGroup(a) - getDisplayGroup(b) ||
            getRank(a) - getRank(b) ||
            a.symbol.localeCompare(b.symbol)
    );

const AssetFields = ({ value: selectedAsset = null, onChange = () => {}, error }) => {
    const [assetSearch, setAssetSearch] = useState('');
    const [activeAssetTypes, setActiveAssetTypes] = useState(
        defaultActiveAssetTypes
    );

    const filteredAssets = useMemo(() => {
        const search = assetSearch.trim().toLowerCase();
        const noneActive = Object.values(activeAssetTypes).every((v) => !v);
        const activeAssets = noneActive
            ? assets
            : assets.filter((asset) => activeAssetTypes[asset.type]);

        if (!search) {
            return activeAssets;
        }

        return activeAssets.filter((asset) =>
            (
                asset.searchText ||
                `${asset.symbol} ${asset.name}`.toLowerCase()
            ).includes(search)
        );
    }, [activeAssetTypes, assetSearch]);

    const toggleAssetType = (type) => {
        setActiveAssetTypes((currentFilters) => ({
            ...currentFilters,
            [type]: !currentFilters[type],
        }));
    };

    const visibleAssets = useMemo(
        () => filteredAssets.slice(0, ASSET_DISPLAY_LIMIT),
        [filteredAssets]
    );

    return (
        <div id="asset-column" className="form-column">
            <label htmlFor="asset">1. Select An Asset</label>
            <input
                id="asset"
                name="asset"
                type="search"
                value={assetSearch}
                placeholder="Search assets..."
                onChange={(e) => setAssetSearch(e.target.value)}
            />
            <div
                className="asset-filter-options"
                role="group"
                aria-label="Asset types"
            >
                {assetFilterOptions.map((option) => (
                    <button
                        key={option.type}
                        type="button"
                        className="asset-filter"
                        aria-pressed={activeAssetTypes[option.type]}
                        onClick={() => toggleAssetType(option.type)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
            {selectedAsset && (
                <div className="asset-selected">
                    <div className="asset-selected-header">
                        <p className="asset-selected-label">Selected Asset</p>
                        <button
                            type="button"
                            className="asset-selected-clear"
                            aria-label={`Remove ${selectedAsset.name}`}
                            onClick={() => onChange(null)}
                        >
                            x
                        </button>
                    </div>
                    <div className="asset-selected-body">
                        {selectedAsset.logoUrl ? (
                            <img
                                src={selectedAsset.logoUrl}
                                alt=""
                                className="asset-selected-logo"
                                aria-hidden="true"
                            />
                        ) : (
                            <span
                                className="asset-selected-logo"
                                aria-hidden="true"
                            >
                                {selectedAsset.symbol.toUpperCase()}
                            </span>
                        )}
                        <div className="asset-selected-info">
                            <span className="asset-selected-name">
                                {selectedAsset.name}
                            </span>
                            <div className="asset-selected-meta">
                                <span>
                                    {selectedAsset.symbol.toUpperCase()}
                                </span>
                                <span>{selectedAsset.type.toUpperCase()}</span>
                                {selectedAsset.exchange && (
                                    <span>{selectedAsset.exchange}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {error && (
                <p className="form-validation-error" id="asset-error">
                    {error}
                </p>
            )}
            <div
                className="asset-list"
                role="listbox"
                aria-describedby={error ? 'asset-error' : undefined}
                aria-invalid={Boolean(error)}
            >
                {visibleAssets.map((asset) => (
                    <button
                        key={asset.id}
                        type="button"
                        className={`asset-option${
                            selectedAsset?.id === asset.id ? ' selected' : ''
                        }`}
                        role="option"
                        aria-selected={selectedAsset?.id === asset.id}
                        onClick={() => {
                            onChange(asset);
                        }}
                    >
                        {asset.logoUrl ? (
                            <img
                                src={asset.logoUrl}
                                alt=""
                                className="asset-logo"
                                aria-hidden="true"
                            />
                        ) : (
                            <span className="asset-logo" aria-hidden="true">
                                {asset.symbol.toUpperCase()}
                            </span>
                        )}
                        <span className="asset-copy">
                            <span className="asset-name">{asset.name}</span>
                            <span className="asset-symbol">
                                ({asset.symbol.toUpperCase()})
                            </span>
                            <span className="asset-type">
                                {asset.type.toUpperCase()}
                            </span>
                        </span>
                    </button>
                ))}
                {filteredAssets.length > ASSET_DISPLAY_LIMIT && (
                    <p className="asset-empty">
                        Showing {ASSET_DISPLAY_LIMIT} of {filteredAssets.length}
                        . Search to narrow the list.
                    </p>
                )}
                {filteredAssets.length === 0 && (
                    <p className="asset-empty">No assets found</p>
                )}
            </div>
        </div>
    );
};

export default AssetFields;
